type Locale = "de" | "en";

type City = {
  name: string;
  km: number;
  bearing: number; // degrees from north, clockwise
  labelAnchor?: "start" | "middle" | "end";
  labelDx?: number;
  labelDy?: number;
};

// 0.7 px per km — keeps München (290 km road → 203 px) inside the 600 viewBox.
const SCALE = 0.7;
const CX = 300;
const CY = 300;

const RINGS = [50, 100, 200, 300];

const CITIES: City[] = [
  { name: "Würzburg", km: 25, bearing: 160, labelAnchor: "start", labelDx: 8, labelDy: 4 },
  { name: "Schweinfurt", km: 40, bearing: 45, labelAnchor: "start", labelDx: 8, labelDy: 4 },
  { name: "Aschaffenburg", km: 50, bearing: 255, labelAnchor: "end", labelDx: -8, labelDy: 4 },
  { name: "Frankfurt", km: 100, bearing: 245, labelAnchor: "end", labelDx: -8, labelDy: 4 },
  { name: "Nürnberg", km: 100, bearing: 135, labelAnchor: "start", labelDx: 8, labelDy: 4 },
  { name: "Stuttgart", km: 180, bearing: 200, labelAnchor: "end", labelDx: -8, labelDy: 4 },
  { name: "München", km: 290, bearing: 155, labelAnchor: "start", labelDx: 8, labelDy: 4 },
];

function polar(km: number, bearingDeg: number) {
  const r = km * SCALE;
  const rad = (bearingDeg * Math.PI) / 180;
  return { x: CX + r * Math.sin(rad), y: CY - r * Math.cos(rad) };
}

export function EinzugsgebietMap({ locale }: { locale: Locale }) {
  const heading = locale === "de" ? "Einzugsgebiet" : "Catchment area";
  const calvariusLabel = "Calvarius · Karlstadt am Main";
  const titleId = "einzugsgebiet-title";
  const descId = "einzugsgebiet-desc";

  const beyondText =
    locale === "de"
      ? "Darüber hinaus bundesweit und DACH-weit digital — Berlin · Hamburg · Köln · Wien · Zürich"
      : "Beyond that nationally and DACH-wide digital — Berlin · Hamburg · Cologne · Vienna · Zurich";

  const fullText =
    locale === "de"
      ? "Calvarius arbeitet aus Karlstadt am Main heraus mit Mandate-Kunden in Würzburg (25 km), Schweinfurt (40 km), Aschaffenburg (50 km), Frankfurt am Main (100 km), Nürnberg (100 km), Stuttgart (180 km) und München (290 km). Bundesweit und DACH-weit zusätzlich digital."
      : "Calvarius operates from Karlstadt am Main with mandate clients in Würzburg (25 km), Schweinfurt (40 km), Aschaffenburg (50 km), Frankfurt am Main (100 km), Nürnberg (100 km), Stuttgart (180 km), and Munich (290 km). Nationally and DACH-wide additionally digital.";

  const title =
    locale === "de"
      ? "Schematische Distanz-Karte des Einzugsgebiets von Calvarius"
      : "Schematic catchment-area map of Calvarius";

  const desc =
    locale === "de"
      ? "Schematische Karte mit konzentrischen Distanz-Ringen bei 50, 100, 200 und 300 Kilometern. Calvarius in Karlstadt am Main im Zentrum, umgeben von Würzburg (25 km), Schweinfurt (40 km), Aschaffenburg (50 km), Frankfurt am Main (100 km), Nürnberg (100 km), Stuttgart (180 km) und München (290 km)."
      : "Schematic map with concentric distance rings at 50, 100, 200 and 300 kilometres. Calvarius in Karlstadt am Main at the centre, surrounded by Würzburg (25 km), Schweinfurt (40 km), Aschaffenburg (50 km), Frankfurt am Main (100 km), Nürnberg (100 km), Stuttgart (180 km) and Munich (290 km).";

  return (
    <div className="einzugsgebiet-section">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">
        {heading}
      </div>

      <svg
        className="einzugsgebiet-svg mt-4"
        viewBox="0 0 600 600"
        role="img"
        aria-labelledby={`${titleId} ${descId}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <title id={titleId}>{title}</title>
        <desc id={descId}>{desc}</desc>

        {/* Distance rings */}
        {RINGS.map((km) => (
          <circle
            key={km}
            cx={CX}
            cy={CY}
            r={km * SCALE}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.18}
            strokeDasharray="3 4"
            className="text-ink-3"
          />
        ))}

        {/* Ring labels (top axis) */}
        {RINGS.map((km) => (
          <text
            key={`label-${km}`}
            x={CX}
            y={CY - km * SCALE - 5}
            textAnchor="middle"
            className="ring-label"
          >
            {km} km
          </text>
        ))}

        {/* Connection lines from center to each city */}
        {CITIES.map((c) => {
          const p = polar(c.km, c.bearing);
          return (
            <line
              key={`line-${c.name}`}
              x1={CX}
              y1={CY}
              x2={p.x}
              y2={p.y}
              stroke="currentColor"
              strokeOpacity={0.12}
              className="text-ink-3"
            />
          );
        })}

        {/* Calvarius marker — Lime with halo */}
        <circle cx={CX} cy={CY} r={18} className="fill-lime/20" />
        <circle cx={CX} cy={CY} r={10} className="fill-lime/40" />
        <circle cx={CX} cy={CY} r={5} className="fill-lime-deep" />
        <text
          x={CX}
          y={CY - 26}
          textAnchor="middle"
          className="calvarius-label"
        >
          {calvariusLabel}
        </text>

        {/* City markers */}
        {CITIES.map((c) => {
          const p = polar(c.km, c.bearing);
          return (
            <g key={c.name}>
              <circle cx={p.x} cy={p.y} r={4} className="fill-ink" />
              <text
                x={p.x + (c.labelDx ?? 0)}
                y={p.y + (c.labelDy ?? 0)}
                textAnchor={c.labelAnchor ?? "start"}
                className="city-label"
              >
                {c.name} · {c.km} km
              </text>
            </g>
          );
        })}

        {/* Beyond-Hinweis am unteren Rand */}
        <text x={CX} y={585} textAnchor="middle" className="beyond-label">
          {beyondText}
        </text>
      </svg>

      <p className="einzugsgebiet-fulltext">{fullText}</p>
    </div>
  );
}
